///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shared building blocks for the anchored info panels: the label/value
/// field, section titles, status badges, the coordinate footer, the
/// relative-time formatter, the "Current Conditions" weather block, and
/// the compact-mode "More" expander. Extracted from the per-panel
/// copies so the styling stays consistent across all datasets.
///

import { useCurrentWeatherGrid } from "@/earthbrowser/dataset-distribution/weather-grid"
import { convertTemperature, formatSpeed } from "@/earthbrowser/utils/unitFormatters"
import { cn } from "@/utils/utils"
import { createContext, useContext, useState } from "react"

/**
 * Mobile bottom-sheet expansion, provided by AnchoredPanelShell so the
 * "More details" toggle and the sheet's peek/expanded state are one thing:
 * opening the details grows the sheet, collapsing the sheet hides them.
 * Null outside the sheet (desktop), where MoreSection keeps local state.
 */
export interface SheetExpansion {
	expanded: boolean
	setExpanded: (expanded: boolean) => void
}

export const SheetExpansionContext = createContext<SheetExpansion | null>(null)

/** Two-tone label/value pair for the details grids */
export function InfoField({ label, value }: { label: string; value: string }) {
	return (
		<div className="min-w-0">
			<span className="text-slate-500">{label} </span>
			<span className="text-slate-200 truncate">{value}</span>
		</div>
	)
}

/** Small-caps section heading in the panel's accent color */
export function SectionTitle({ accentClass, children }: { accentClass: string; children: React.ReactNode }) {
	return (
		<div className={cn("text-[11px] font-semibold uppercase tracking-[0.18em]", accentClass)}>
			{children}
		</div>
	)
}

/** Pill-shaped status badge (alert level, category, staleness, ...) */
export function Badge({ colorClass, children }: { colorClass: string; children: React.ReactNode }) {
	return (
		<span className={cn("text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full", colorClass)}>
			{children}
		</span>
	)
}

/** Monospace coordinate footer; extra appends e.g. depth or owner */
export function CoordFooter({ lat, lon, extra, className }: {
	lat: number
	lon: number
	extra?: string
	className?: string
}) {
	return (
		<div className={cn("text-[10px] font-mono text-slate-500", className)}>
			{Math.abs(lat).toFixed(4)}° {lat >= 0 ? "N" : "S"},{" "}
			{Math.abs(lon).toFixed(4)}° {lon >= 0 ? "E" : "W"}
			{extra ? `, ${extra}` : ""}
		</div>
	)
}

/** Compact relative time; clamps future timestamps to "just now" */
export function formatTimeAgo(date: Date): string {
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
	if (seconds < 60) return "just now"
	const minutes = Math.floor(seconds / 60)
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return `${hours}h ago`
	const days = Math.floor(hours / 24)
	if (days < 30) return `${days}d ago`
	const months = Math.floor(days / 30)
	if (months < 12) return `${months}mo ago`
	return `${Math.floor(months / 12)}y ago`
}

/**
 * Live weather at a point: temperature + description on the left, wind +
 * humidity on the right. Owns its own weather-grid query so panels only
 * pass coordinates and their accent color.
 */
export function CurrentConditionsSection({ lat, lon, accentClass, errorClass = "text-red-400" }: {
	lat: number
	lon: number
	accentClass: string
	errorClass?: string
}) {
	const { data: currentWeather, isLoading, isError } = useCurrentWeatherGrid(lat, lon)
	const current = currentWeather?.current

	return (
		<div className="space-y-1.5">
			<SectionTitle accentClass={accentClass}>Current Conditions</SectionTitle>
			{isLoading ? (
				<div className="text-xs text-slate-400">Loading weather...</div>
			) : isError ? (
				<div className={cn("text-xs", errorClass)}>Unable to load weather</div>
			) : (
				<div className="flex items-start justify-between gap-3">
					<div>
						<div className="text-2xl leading-none font-light text-white">
							{current?.temperature?.value != null ? `${Math.round(convertTemperature(current.temperature.value, 0))}°` : "--°"}
						</div>
						<div className="mt-1 text-xs text-slate-300">
							{current?.weather?.description ?? "--"}
						</div>
					</div>
					<div className="text-right text-[11px] text-slate-400 space-y-0.5">
						<div>
							Wind {current?.wind?.speed != null
								? `${formatSpeed(current.wind.speed)} ${current.wind.cardinal ?? ""}`
								: "--"}
						</div>
						<div>
							Humidity {current?.humidity != null ? `${current.humidity}%` : "--"}
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

/**
 * Compact-mode detail gate: on desktop renders children directly; on
 * mobile hides them behind a "More details" toggle so panels stay small.
 * Inside the mobile bottom sheet the toggle drives the sheet's expansion
 * state instead of local state.
 */
export function MoreSection({ compact, children }: { compact: boolean; children: React.ReactNode }) {
	const [localOpen, setLocalOpen] = useState(false)
	const sheet = useContext(SheetExpansionContext)
	const open = sheet ? sheet.expanded : localOpen
	const setOpen = sheet ? sheet.setExpanded : setLocalOpen

	if (!compact) return <>{children}</>

	return (
		<div className="space-y-3">
			<button
				onClick={() => setOpen(!open)}
				className="pointer-events-auto flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
				aria-expanded={open}
			>
				<svg
					width="8" height="8" viewBox="0 0 8 8" fill="currentColor"
					className={cn("transition-transform duration-200", open && "rotate-90")}
				>
					<path d="M2 0 L7 4 L2 8 Z" />
				</svg>
				{open ? "Less" : "More details"}
			</button>
			{open && children}
		</div>
	)
}
